import _asyncToGenerator from "/vercel/workpath0/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "/vercel/workpath0/node_modules/@babel/runtime/regenerator";

/**
 * @license
 * Copyright 2019 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { convertToTensor } from '../tensor_util_env';
import { assert, assertShapesMatch, getTypedArrayFromDType } from '../util';
import { tensor } from './tensor';
/**
 * Returns whether the targets are in the top K predictions.
 *
 * ```js
 * const predictions = tf.tensor2d([[20, 10, 40, 30], [30, 50, -20, 10]]);
 * const targets = tf.tensor1d([2, 0]);
 * const precision = await tf.inTopKAsync(predictions, targets);
 * precision.print();
 * ```
 * @param predictions 2-D or higher `tf.Tensor` with last dimension being
 *     at least `k`.
 * @param targets 1-D or higher `tf.Tensor`.
 * @param k Optional Number of top elements to look at for computing precision,
 *     default to 1.
 *
 * @doc {heading: 'Operations', subheading: 'Evaluation'}
 */

function inTopKAsync_(_x, _x2) {
  return _inTopKAsync_.apply(this, arguments);
}

function _inTopKAsync_() {
  _inTopKAsync_ = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(predictions, targets) {
    var k,
        $predictions,
        $targets,
        lastDim,
        predictionsVals,
        targetsVals,
        batch,
        size,
        precision,
        b,
        offset,
        vals,
        valAndInd,
        i,
        _i,
        _args = arguments;

    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            k = _args.length > 2 && _args[2] !== undefined ? _args[2] : 1;
            $predictions = convertToTensor(predictions, 'predictions', 'inTopK');
            $targets = convertToTensor(targets, 'targets', 'inTopK');
            assert($predictions.rank > 1, function () {
              return 'inTopK() expects the predictions to be of rank 2 or higher, ' + "but got ".concat($predictions.rank);
            });
            assert($predictions.rank - 1 === $targets.rank, function () {
              return "predictions rank should be 1 larger than " + "targets rank, but got predictions rank " + "".concat($predictions.rank, " and targets rank ").concat($targets.rank);
            });
            assertShapesMatch($predictions.shape.slice(0, $predictions.shape.length - 1), $targets.shape, "predictions's shape should be align with the targets' shape, " + 'except the last dimension.');
            lastDim = $predictions.shape[$predictions.shape.length - 1];
            assert(k > 0 && k <= lastDim, function () {
              return "'k' passed to inTopK() must be > 0 && <= the predictions last " + "dimension (".concat(lastDim, "), but got ").concat(k);
            });
            _context.next = 10;
            return $predictions.data();

          case 10:
            predictionsVals = _context.sent;
            _context.next = 13;
            return $targets.data();

          case 13:
            targetsVals = _context.sent;
            // Reshape predictionsVals into a 2d tensor [batch, lastDim]
            // and look up topK along lastDim.
            batch = predictionsVals.length / lastDim, size = lastDim;
            precision = getTypedArrayFromDType('bool', batch);
            b = 0;

          case 17:
            if (!(b < batch)) {
              _context.next = 35;
              break;
            }

            offset = b * size;
            vals = predictionsVals.subarray(offset, offset + size);
            valAndInd = [];

            for (i = 0; i < vals.length; i++) {
              valAndInd.push({
                value: vals[i],
                index: i
              });
            }

            valAndInd.sort(function (a, b) {
              return b.value - a.value;
            });
            precision[b] = 0;
            _i = 0;

          case 25:
            if (!(_i < k)) {
              _context.next = 32;
              break;
            }

            if (!(valAndInd[_i].index === targetsVals[b])) {
              _context.next = 29;
              break;
            }

            precision[b] = 1;
            return _context.abrupt("break", 32);

          case 29:
            _i++;
            _context.next = 25;
            break;

          case 32:
            b++;
            _context.next = 17;
            break;

          case 35:
            if (predictions !== $predictions) {
              $predictions.dispose();
            }

            if (targets !== $targets) {
              $targets.dispose();
            } // Output precision has the same shape as targets.


            return _context.abrupt("return", tensor(precision, $targets.shape, 'bool'));

          case 38:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _inTopKAsync_.apply(this, arguments);
}

export var inTopKAsync = inTopKAsync_;